import useConsentCheck from '@/app/hooks/useConsentCheck'
import { useIntersectionObserver } from '@/app/hooks/useIntersectionObserver'
import { useTranslation } from '@/app/i18n/client'
import { NewsDetail } from '@/app/types/newsType'
import ShareSocials from '@/components/ui/ShareSocials'
import { decodeHTMLEntities } from '@/lib/utils'
import Image from 'next/image'
import Link from 'next/link'
import { useParams } from 'next/navigation'
import React, { MutableRefObject, useEffect, useRef } from 'react'
import sanitizeHtml from 'sanitize-html'
import CountryTag from '../../CountryTag'
import { gtmNewsCardViewTracker } from '../../GTMTrackers/GTMNewsTracker'

const NewsCardCompany: React.FC<{ newsData: NewsDetail }> = ({ newsData }) => {
  const { t } = useTranslation()
  const consentUserValue = useConsentCheck()

  const params = useParams<{ lng: string }>()
  const newsCardRef: MutableRefObject<HTMLDivElement | null> = useRef(null)
  const handleIntersection = () => {
    gtmNewsCardViewTracker(
      newsData?.id,
      String(newsData?.company_id),
      params?.lng ?? 'en',
      newsData?.language_id,
      consentUserValue()
    )
  }

  const observeElement = useIntersectionObserver(handleIntersection)

  useEffect(() => {
    if (newsCardRef.current) {
      observeElement(newsCardRef.current)
    }
  }, [observeElement])
  return (
    <div
      ref={newsCardRef}
      className=" border border-[#ededed] p-3 cursor-pointer hover:border-[#d4d4d4] my-[20px] rounded"
    >
      <Link
        prefetch={false}
        href={`/${params?.lng}/news/news-detail/${newsData?.id}`}
      >
        <div className="relative flex gap-8 w-full mobile:flex-col mobile:gap-[10px]">
          <div className="basis-1/5 !w-[197px] !h-[166px] bg-[#ededed] rounded-[5px] overflow-hidden">
            <Image
              width={197}
              height={166}
              className={`!w-[197px] !h-[166px] ${newsData?.images[0]?.url ? 'bg-cover' : 'bg-contain'}`}
              src={
                newsData?.images?.length > 0
                  ? newsData?.images[0]?.url
                  : '/assets/Icons/noImage/noImage2.svg'
              }
              alt="news-image"
            />
          </div>
          <div
            className="absolute top-[5px] right-[5px]"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
          >
            <ShareSocials
              sharAbleLink={`${window.location.origin}/${params?.lng}/news/news-detail/${newsData?.id}`}
              shareText={newsData?.title?.slice(0, 50)}
              shareBg={false}
              share_id={newsData?.id}
            />
          </div>
          <div className="basis-4/5 flex flex-col justify-between">
            <div className="flex flex-col">
              <h1 className="text-[18px] font-ns_bold max-w-full overflow-hidden text-ellipsis">
                {decodeHTMLEntities(newsData.title)}
              </h1>
              <div className="flex items-center gap-[13px] mt-[8px]">
                <CountryTag name={newsData?.origin} size="xs" />

                <p className="text-xs text-[#4e4e4e]">
                  {newsData?.published_at ? newsData?.published_at : ''}
                </p>
              </div>
              <p
                className="text-[#666] text-sm py-3 text-wrap"
                dangerouslySetInnerHTML={{
                  __html: newsData ? sanitizeHtml(newsData?.caption) : '',
                }}
              />
            </div>
            <p className="text-sm text-primary font-ns_semibold">
              {t('Read More')} &gt;&gt;
            </p>
          </div>
        </div>
      </Link>
    </div>
  )
}

export default NewsCardCompany
